import { useEffect, useState } from "react";

/**
 * Compact nav (hamburger + off-canvas sidebar) — standard interaction-media approach
 * (MDN / web.dev): do not infer “tablet” from laptop-like width×height alone.
 *
 * Matches when:
 * 1) Narrow viewport ≤1100px — phones, most tablet portrait (incl. iPad Pro 13 at 1032),
 *    and tight split windows. Laptops at 100% / typical 125% zoom stay above this.
 * 2) Touch tablets up to 1400px (any orientation) via hover: none + pointer: coarse —
 *    covers iPad Pro landscape (~1376) without pulling in trackpad laptops.
 *
 * CSS @media lists must stay identical to COMPACT_NAV_QUERY in:
 * Header, MobileHeader, Sidebar, ProjectDashboard, TopNavigation, SearchBar,
 * RecentDashboards.
 */
/** Keep in sync with pwaUtils MOBILE_MAX_WIDTH / shouldShowPwaMobileUi. */
export const COMPACT_NAV_MAX_WIDTH = 1100;

export const COMPACT_NAV_QUERY = [
  `(max-width: ${COMPACT_NAV_MAX_WIDTH}px)`,
  "(max-width: 1400px) and (hover: none) and (pointer: coarse)",
].join(", ");

export function isCompactNav() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  return window.matchMedia(COMPACT_NAV_QUERY).matches;
}

export function useCompactNav() {
  const [compact, setCompact] = useState(isCompactNav);

  useEffect(() => {
    const media = window.matchMedia(COMPACT_NAV_QUERY);
    const update = () => setCompact(media.matches);
    update();
    media.addEventListener("change", update);
    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);
    return () => {
      media.removeEventListener("change", update);
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
    };
  }, []);

  return compact;
}
